import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import _ from 'lodash';

import AppBar from 'components/AppBar';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import FloatingButton from 'components/FloatingButton';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Item from 'components/List/StyledListItem';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import Pagination from 'components/Pagination';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import SearchWithApi from 'components/SearchWithApi';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { GroupedListModel } from 'models/groupedList';
import { htmlT } from 'utils/generic';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';


import {
  Container,
  Content,
  StyledButton,
  ButtonContainer,
  List,
  SubTitle,
  BoxContainer,
  ButtonGhost,
  ButtonGhostIcon,
  LoadingContainer,
  AlertMessageContainer,
} from './styles';
import OptionsItems from './Components/OptionsItems';
import OptionsItemsUserType from './Components/OptionsItemsUserType';

interface Props {
  title: string;
  subTitle?: string;
  userList: GroupedListModel[];
  isModal?: boolean;
  alreadySelected?: GroupedListModel[];
  isLoading: boolean;
  isApprovalSelection?: boolean;
  selectableType: 'arrow' | 'check' | 'radio' | 'trashcan' | null;
  onConfirmSelection: (users: GroupedListModel[]) => void;
  onCloseSelectionScreen: () => void;
  singleSelect?: boolean;
  buttonLabel?: string;
  noAdd?: boolean;
  onAdd?: () => void;
  addIcon?: string;
  addLabel?: string;
  hasFavorites?: boolean;
  onDelete?: (users: GroupedListModel[]) => void;
  loadUsers?: (currentPage: number, userName?: string, status?: string, userType?: string) => void;
  totalUsers?: number;
  currentPage?: number;
  maxItemsPerPage?: number;
  userSelectionOrigin: 'user' | 'userGroup' | 'travelArranger' | 'approvers' | 'branchCompanies';
  maxSelectedItems?: number;
  maxSelectedItemsByHotel?: boolean;
  hideUserStatus?: boolean;
}

const UserList = ({
  title,
  subTitle,
  userList,
  isLoading,
  isModal,
  onConfirmSelection,
  onCloseSelectionScreen,
  alreadySelected,
  selectableType,
  isApprovalSelection = false,
  singleSelect,
  buttonLabel,
  noAdd = true,
  onAdd,
  addIcon,
  addLabel,
  hasFavorites,
  onDelete,
  loadUsers,
  totalUsers,
  currentPage,
  maxItemsPerPage,
  userSelectionOrigin,
  maxSelectedItems,
  maxSelectedItemsByHotel,
  hideUserStatus
}: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const [usersResult, setUsersResult] = useState<GroupedListModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<GroupedListModel[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showBottomModal, setShowBottomModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<GroupedListModel>();
  const searchResultsText = htmlT(t, 'group.groupAggregation.searchResults', { count: usersResult?.length });
  const userAccount = useUserAccount();
  const userListAfterSelected = userList.filter((user) => !selectedUser.find((selected) => user.id === selected.id));
  const [query, setQuery] = useState('');
  const statusRef = useRef(null);
  const [statusText, setStatusText] = useState<string>('');
  const [statusValue, setStatusValue] = useState<string>('active');
  const userTypeRef = useRef(null);
  const [userTypeText, setUserTypeText] = useState<string>('');
  const [userTypeValue, setUserTypeValue] = useState<string>('all');

  useEffect(() => {
    if (alreadySelected) setSelectedUser(alreadySelected);
  }, [alreadySelected]);

  const handleSelection = (user: GroupedListModel) => {
    const userFound = selectedUser.find((item) => item.id === user.id);
    setCurrentUser(user);

    if (singleSelect) {
      if (!userFound) {
        setSelectedUser([user]);
      }
    } else {
      if (selectableType === 'arrow') {
        onConfirmSelection([user]);
      } else if (user.userGroupId && !userFound && userSelectionOrigin === 'userGroup') {
        setShowBottomModal(true);
      } else if (userFound) {
        setSelectedUser(selectedUser.filter((item) => item.id !== user.id));
      } else {
        if (maxSelectedItems !== undefined && selectedUser.length + 1 > maxSelectedItems) {
          return;
        }
        setSelectedUser([...selectedUser, user]);
      }
    }
  };

  const handleSelectionConfirm = () => {
    if (currentUser) {
      setSelectedUser([...selectedUser, currentUser]);
    }
  };

  const handleSubmitSelection = () => {
    if (selectedUser) onConfirmSelection(selectedUser);
    onCloseSelectionScreen();
  };

  const handleOnSearch = useCallback(
    (value: string) => {
      if (value) {
        setUsersResult(
          userListAfterSelected.filter(
            (item: GroupedListModel) =>
              `${item.name} ${item.lastName}`?.toLowerCase().includes(value.toLowerCase()) ||
              item.email?.toLowerCase().includes(value.toLowerCase()),
          ),
        );
        setShowSearch(true);
      } else {
        setUsersResult([]);
        setShowSearch(false);
      }
    },
    [userListAfterSelected],
  );

  const onClickDelete = (users: GroupedListModel[]) => {
    if (onDelete !== undefined) {
      onDelete(users);
    }
    return;
  };

  const handleOnSearchNew = _.debounce((value: string, statusValueParam: string) => {
    const newValue = statusValueParam !== undefined ? query : value;
    setQuery(newValue);
    if (loadUsers !== undefined) {
      loadUsers(1, newValue, statusValueParam ?? statusValue, userTypeValue);
    } else {
      handleOnSearch(newValue);
    }
  }, 750);

  const handleOnSearchNewUserType = _.debounce((value: string, userTypeValueParam: string) => {
    const newValue = userTypeValueParam !== undefined ? query : value;
    setQuery(newValue);
    if (loadUsers !== undefined) {
      loadUsers(1, newValue, statusValue, userTypeValueParam ?? userTypeValue);
    } else {
      handleOnSearch(newValue);
    }
  }, 750);

  const userStatusList: { key: string; value: string }[] = 
  [
    { key: 'all', value: t('usersList.dropdown.all') },
    { key: 'active', value: t('usersList.dropdown.active') },
    { key: 'inactive', value: t('usersList.dropdown.inactive') }
  ];

  const userTypeList: { key: string; value: string }[] = 
  [
    { key: 'all', value: t('usersList.dropdownUserType.userType') },
    { key: 'all', value: t('usersList.dropdownUserType.all') },
    { key: 'admin', value: t('usersList.dropdownUserType.admin') },
    { key: 'traveler', value: t('usersList.dropdownUserType.traveler') },
    { key: 'invited', value: t('usersList.dropdownUserType.invited') },
    { key: 'organizer', value: t('usersList.dropdownUserType.organizer') },
    { key: 'approver', value: t('usersList.dropdownUserType.approver') },
  ];

  const handleSelectStatus = (name: string) => {
    
    const status = userStatusList.find((g) => g.key === name);
    setStatusText(status?.value ?? '');
    setStatusValue(status?.key ?? 'active');
  };

  const handleSelectUserType = (name: string) => {
    
    const status = userTypeList.find((g) => g.key === name);
    setUserTypeText(status?.value ?? '');
    setUserTypeValue(status?.key ?? 'all');
  };

  const GetTextMaxPax = () => {
    if (maxSelectedItemsByHotel !== undefined) {
      return t('travelArrangerSelector.maxSelectedMessageHotel', { total: (maxSelectedItems - selectedUser.length) });
    }
    else {
      return t('travelArrangerSelector.maxSelectedMessageAir', { total: (maxSelectedItems - selectedUser.length) });
    }
  }

  return (
    <Container>
      <Mobile>
        <AppBar goBack={onCloseSelectionScreen}>
          <h5>{title}</h5>
          <SubTitle>
            {selectableType === 'arrow' && !isLoading ? t('users.subTitle', { number: userList.length }) : subTitle}
          </SubTitle>
        </AppBar>
      </Mobile>

      {isLoading && (
        <LoadingContainer>
          <Loader fullScreen={!isModal} />
        </LoadingContainer>
      )}
      <>
        <Desktop>
          {isModal ? (
            <ModalHeader title={title} onClose={onCloseSelectionScreen} />
          ) : (
            <ScreenHeader title={t('userMenu.usersProfilesTitle')} subtitle={t('userMenu.usersProfilesDetail')} />
          )}
        </Desktop>

        {!isMobile && !isModal && (
          <>
            <div style={ { display: 'flex', paddingBottom: '26px' } }>
              <div>
                <SearchWithApi show={true} onSearch={handleOnSearchNew} />
              </div>
              <div style={ { flex: 1, paddingLeft: '16px', maxWidth: '200px', display: (isLoading ? 'none' : 'block') } }>
                {hideUserStatus && (
                <CustomSelect
                  name="travelStatus"
                  onChange={handleSelectStatus}
                  afterOnChange={handleOnSearchNew}
                  label=""
                  placeHolder={t('usersList.dropdown.active')}
                  selectRef={statusRef}
                  value={statusText}
                >
                  {({ ...selectProps }: CustomSelectRenderProps) => (
                    <OptionsItems {...selectProps} selectRef={statusRef} userStatus={userStatusList} />
                  )}
                </CustomSelect>
                )}
              </div>
              <div style={ { flex: 1, paddingLeft: '16px', maxWidth: '200px', display: (isLoading ? 'none' : 'block') } }>
                {
                <CustomSelect
                  name="userType"
                  onChange={handleSelectUserType}
                  afterOnChange={handleOnSearchNewUserType}
                  label=""
                  placeHolder={t('usersList.dropdownUserType.userType')}
                  selectRef={userTypeRef}
                  value={userTypeText}
                >
                  {({ ...selectProps }: CustomSelectRenderProps) => (
                    <OptionsItemsUserType {...selectProps} selectRef={userTypeRef} userType={userTypeList} />
                  )}
                </CustomSelect>
                }
              </div>
              <div style={ { flex: 1 } }>
                <></>
              </div>
            </div>            
            
            <List>
              {(showSearch ? usersResult : userList).map((user) => (
                <Item
                  isActive={!user.isActive}
                  key={`user-${user.id}`}
                  title={`${user.name} ${user.lastName}`}
                  onClick={() => onConfirmSelection([user])}
                  groupName={user.userGroupName || t('usersList.groupTagEmpty')}
                  description={user.email}
                  groupNameActive={!!user.userGroupName}
                  deleteMode={userAccount?.type === 'admin' ? true : false}
                  forceEdit={true}
                  onDelete={() => onClickDelete([user])}
                  userType={user.type}
                />
              ))}
            </List>
          </>
        )}

        {(isMobile || isModal) && (
          <Content>
            {!noAdd && (
              <BoxContainer>
                <ButtonGhost onClick={onAdd}>
                  <ButtonGhostIcon src={addIcon} />
                  {addLabel}
                </ButtonGhost>
              </BoxContainer>
            )}
            <SearchWithApi show={true} onSearch={handleOnSearchNew} fullWidth={isModal} />
            {hideUserStatus && (
              <>
                <div style={ { paddingLeft: '18px', paddingRight: '18px', paddingBottom: '16px', display: (isLoading ? 'none' : 'block') } }>
                  <CustomSelect
                    name="travelStatus"
                    onChange={handleSelectStatus}
                    afterOnChange={handleOnSearchNew}
                    label=""
                    placeHolder={t('usersList.dropdown.active')}
                    selectRef={statusRef}
                    value={statusText}
                  >
                    {({ ...selectProps }: CustomSelectRenderProps) => (
                      <OptionsItems {...selectProps} selectRef={statusRef} userStatus={userStatusList} />
                    )}
                  </CustomSelect>
                </div>  
                <div style={ { paddingLeft: '18px', paddingRight: '18px', paddingBottom: '16px', display: (isLoading ? 'none' : 'block') } }>
                  <CustomSelect
                      name="userType"
                      onChange={handleSelectUserType}
                      afterOnChange={handleOnSearchNewUserType}
                      label=""
                      placeHolder={t('usersList.dropdownUserType.userType')}
                      selectRef={userTypeRef}
                      value={userTypeText}
                    >
                      {({ ...selectProps }: CustomSelectRenderProps) => (
                        <OptionsItemsUserType {...selectProps} selectRef={userTypeRef} userType={userTypeList} />
                      )}
                  </CustomSelect>
                </div>
              </>
            )}
            {showSearch ? (
              <List>
                <GroupedItem
                  quantityText={searchResultsText}
                  isSearch={showSearch}
                  items={usersResult}
                  selectedItems={selectedUser}
                  selectableType={selectableType}
                  onSelection={handleSelection}
                />
              </List>
            ) : (
              <GroupedList
                isAlphabetic
                isSelectable
                items={singleSelect ? userList : userListAfterSelected}
                isSearch={showSearch}
                selectableType={selectableType}
                onSelection={handleSelection}
                currentItem={selectedUser}
                selectedQuantityKey="group.groupAggregation.userSelected"
                singleSelect={singleSelect}
                hasFavorites={hasFavorites}
              />
            )}
          </Content>
        )}

        <Pagination
          currentPage={currentPage}
          maxItemsPerPage={maxItemsPerPage}
          total={totalUsers}
          onClick={(page) => {
            loadUsers !== undefined ? loadUsers(page ?? 1, query, statusValue) : null;
          }}
        />

        {((alreadySelected && alreadySelected.length > 0) || selectedUser.length > 0) && (
          <ButtonContainer onClick={handleSubmitSelection}>
            {userSelectionOrigin === 'travelArranger' && selectedUser && selectedUser.length > 0 && (
              <>
                <AlertMessageContainer>
                  <img src="/corporate/images/payment/addcard-warning.svg" alt="" />
                  <span>
                    {htmlT(t, 'travelArrangerSelector.policyAccordingToTheUser', {
                      traveler: `${selectedUser.first().name} ${selectedUser.first().lastName}`,
                    })}
                    {maxSelectedItems !== undefined && selectedUser.length + 1 > maxSelectedItems && (
                      <>
                        <br />
                        <br />
                        {t('travelArrangerSelector.maxSelectedItems', { total: maxSelectedItems })}
                      </>
                    )}
                    {maxSelectedItems !== undefined && maxSelectedItems > selectedUser.length  && (
                      <>
                        <br />
                        <br />
                        {GetTextMaxPax()}
                      </>
                    )}
                  </span>
                </AlertMessageContainer>
              </>
            )}

            <StyledButton disabled={maxSelectedItemsByHotel && maxSelectedItems && maxSelectedItems !== selectedUser.length} value={buttonLabel || t('group.groupAggregation.addApprover')} />
          </ButtonContainer>
        )}

        <ConfirmBottomSheet
          show={showBottomModal}
          title={
            isApprovalSelection ? t('group.groupAggregation.selectApproverTitle') : t('group.groupAggregation.title')
          }
          content={
            isApprovalSelection
              ? htmlT(t, 'group.groupAggregation.confirmApproverMessage', { user: currentUser?.name })
              : htmlT(t, 'group.groupAggregation.userAlreadyIn', { user: currentUser?.name })
          }
          onConfirm={handleSelectionConfirm}
          onClose={() => setShowBottomModal(false)}
        />

        {selectableType === 'arrow' && (
          <FloatingButton zIndex={2} onClick={() => history.push('/corporate/usersForm')} className="target" />
        )}
      </>
    </Container>
  );
};

export default UserList;
